import { inject, Injectable } from '@angular/core';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import { DevPortalHttpClient } from './dev-portal-http-client.service';
import { CmdbApplicationDetails, CmdbApplicationSearchItem } from '../models/service-now/cmdb-application.model';

@Injectable({ providedIn: 'root' })
export class ServiceNowCmdbService {
  private readonly httpClient = inject(DevPortalHttpClient);

  getApplicationById(id: string): Observable<CmdbApplicationDetails | null> {
    const raw = String(id ?? '').trim();
    if (!raw) return of(null);

    return this.httpClient.Get<CmdbApplicationDetails>(`/service-now/cmdb-applications/${encodeURIComponent(raw)}`).pipe(
      map((res) => {
        const name = String(res?.name ?? '').trim();
        return name ? res : null;
      }),
      catchError(() => of(null)),
    );
  }

  searchApplications(query: string): Observable<CmdbApplicationSearchItem[]> {
    const term = String(query ?? '').trim();
    if (!term) return of([]);

    return this.httpClient.Get<CmdbApplicationSearchItem[]>('/service-now/cmdb-applications/search', { query: term }).pipe(
      map((res) => (Array.isArray(res) ? res : [])),
      catchError(() => of([])),
    );
  }

  getApplicationByString(value: string): Observable<CmdbApplicationDetails | null> {
    const term = String(value ?? '').trim();
    if (!term) return of(null);

    return this.searchApplications(term).pipe(
      map((items) => {
        const normalized = term.toLowerCase();
        const exact = (items ?? []).find((x) => {
          const appId = String(x?.appId ?? '').trim().toLowerCase();
          const name = String(x?.name ?? '').trim().toLowerCase();
          return Boolean((appId && appId === normalized) || (name && name === normalized));
        });
        return exact ?? null;
      }),
      switchMap((match) => {
        const appId = String(match?.appId ?? '').trim();
        if (!appId) return of(null);
        return this.getApplicationById(appId);
      }),
      catchError(() => of(null)),
    );
  }
}
